import { EventStatusBadge } from '@/components/events/EventStatusBadge';
import { EventLifecycleButtons } from '@/components/events/EventLifecycleButtons';
import { eventCrudConfig, type Event, type EventStatus } from '@/config/objects/events';
import { CrudListView, type CrudConfig, type CrudListHeaderAction } from '@startupquickstart/frontend';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

interface EventsListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<Event>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (event: Event) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /events
   */
  baseUrl?: string;
  /**
   * Custom header actions (displayed in the header alongside Add/Create buttons)
   */
  headerActions?: CrudListHeaderAction[];
  /**
   * Breadcrumbs for navigation
   */
  breadcrumbs?: Array<{ label: string; href?: string; current?: boolean }>;
}

/**
 * Reusable events list component
 * Pass a custom config with modified actions to customize behavior
 */
export function EventsList({
  config,
  baseUrl = '/events',
  defaultWhere,
  onItemClick,
  className,
  headerActions,
  breadcrumbs,
}: EventsListProps) {
  const navigate = useNavigate();

  // Augment the Status column so each row shows its status badge alongside the
  // primary lifecycle action (Publish / Start / Resume / …). This lets an
  // operator advance an event straight from the overview instead of opening it.
  const effectiveConfig = useMemo<CrudConfig<Event>>(() => {
    const base = config ?? eventCrudConfig;
    return {
      ...base,
      fields: base.fields.map((field) =>
        field.key === 'status'
          ? {
              ...field,
              format: (value: unknown, item?: Event) => (
                // Stacked on phones: badge + lifecycle button side by side
                // is wider than a 390px row can spare next to the name.
                <div className="flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-2">
                  <EventStatusBadge status={value as EventStatus} />
                  {item && (
                    <EventLifecycleButtons
                      event={item}
                      limit={1}
                      size="sm"
                      variantOverride="outline"
                      className="contents"
                    />
                  )}
                </div>
              ),
            }
          : field,
      ),
    };
  }, [config]);

  const _onItemClick = useCallback(
    (event: Event) => {
      if (onItemClick) {
        onItemClick(event);
      } else {
        navigate(`/events/${event.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={effectiveConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        headerActions={headerActions}
        breadcrumbs={breadcrumbs}
        getValueFromItem={(event: Event) => event.id}
        getLabelFromItem={(event: Event) => event.name}
      />
    </div>
  );
}
